import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { products } from '../../data/products';
import ProductImageGallery from '../../components/ProductImageGallery';
import { useCart } from '../../context/CartContext';

const SwitchProController = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('overview');
  const [currentImage, setCurrentImage] = useState('');
  const [selectedColor, setSelectedColor] = useState('Black');
  const [selectedConnectivity, setSelectedConnectivity] = useState('Wireless');
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  
  // 获取控制器产品数据
  const controller = products.find(p => p.name === 'Switch Pro Controller');
  
  // 更多图片
  const additionalImages = [
    controller.image, // 这现在会是 .switch wireless gamepad.jpg
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 143917.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 143938.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 143950.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 144000.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 144008.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 144022.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 144036.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 151810.png",
    "/images/shop-products/switch-pro-controller/Screenshot 2025-05-28 151823.png"
  ];

  // 初始化当前图片
  useEffect(() => {
    setCurrentImage(controller.image);
  }, [controller.image]);

  // 相关产品 - 确保始终有4个产品
  const relatedProducts = products.filter(p => 
    p.name !== 'Switch Pro Controller' && 
    (p.category === 'nintendo-switch' || p.category === 'controllers' || p.category === 'accessories')
  );
  
  // 如果相关产品不足4个，用其他产品补充
  const getRelatedProducts = () => {
    if (relatedProducts.length >= 4) {
      return relatedProducts.slice(0, 4);
    }
    
    // 如果相关产品不足4个，添加其他产品
    const otherProducts = products.filter(p => 
      p.name !== 'Switch Pro Controller' && 
      !relatedProducts.some(rp => rp.id === p.id)
    );
    
    return [...relatedProducts, ...otherProducts].slice(0, 4);
  };
  
  const displayProducts = getRelatedProducts();

  // 处理添加到购物车
  const handleAddToCart = async () => {
    setIsAddingToCart(true);
    
    const productToAdd = {
      ...controller,
      selectedColor,
      selectedConnectivity,
      quantity
    };
    
    addToCart(productToAdd);
    
    // 显示添加成功的反馈
    setTimeout(() => {
      setIsAddingToCart(false);
    }, 1000);
  };

  // 处理立即购买
  const handleBuyNow = () => {
    const productToAdd = {
      ...controller,
      selectedColor,
      selectedConnectivity,
      quantity
    };
    
    addToCart(productToAdd);
    navigate('/cart');
  };

  return (
    <div className="container mx-auto px-4 py-12 text-white">
      {/* 面包屑导航 */}
      <div className="text-sm text-gray-400 mb-6">
        <Link to="/" className="hover:text-white">Home</Link> / 
        <Link to="/shop" className="hover:text-white mx-2">Shop</Link> / 
        <Link to="/shop/controllers" className="hover:text-white mr-2">Controllers</Link> / 
        <span className="text-white">{controller.name}</span>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {/* 左侧产品图片 */}
        <div>
          <ProductImageGallery
            images={additionalImages}
            currentImage={currentImage}
            onImageChange={setCurrentImage}
          />
        </div>
        
        {/* 右侧产品信息 */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{controller.name}</h1>
          
          {/* 评分 */}
          <div className="flex items-center mb-4">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.floor(controller.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
              ))}
            </div>
            <span className="ml-2 text-gray-400">({controller.reviews} reviews)</span>
          </div>
          
          {/* 价格 */}
          <div className="mb-6">
            <span className="text-3xl font-bold">{controller.price}</span>
            {controller.originalPrice && (
              <span className="ml-3 text-gray-400 line-through">{controller.originalPrice}</span>
            )}
            {controller.originalPrice && (
              <span className="ml-3 bg-red-600 text-white px-2 py-1 rounded-md text-sm">
                Save ${(parseFloat(controller.originalPrice.replace(/^S\$/, '')) - parseFloat(controller.price.replace(/^S\$/, ''))).toFixed(2)}
              </span>
            )}
          </div>
          
          {/* 库存状态 */}
          <div className="mb-6">
            {controller.inStock ? (
              <span className="text-green-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                In Stock
              </span>
            ) : (
              <span className="text-red-500 flex items-center">
                <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Out of Stock
              </span>
            )}
          </div>
          
          {/* 简短描述 */}
          <p className="text-gray-300 mb-6">{controller.description}</p>
          
          {/* 选择颜色 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Colour</h3>
            <div className="grid grid-cols-1 gap-3">
              {['Black'].map((color) => (
                <button 
                  key={color}
                  className={`border-2 ${color === selectedColor ? 'border-green-500' : 'border-gray-700'} 
                    rounded-md py-3 px-4 text-center hover:border-green-500 transition-colors`}
                  onClick={() => setSelectedColor(color)}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>
          
          {/* 选择连接方式 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Connectivity</h3>
            <div className="grid grid-cols-1 gap-3">
              {['Wireless'].map((conn) => (
                <button 
                  key={conn}
                  className={`border-2 ${conn === selectedConnectivity ? 'border-green-500' : 'border-gray-700'} 
                    rounded-md py-3 px-4 text-center hover:border-green-500 transition-colors`}
                  onClick={() => setSelectedConnectivity(conn)}
                >
                  {conn}
                </button>
              ))}
            </div>
          </div>
          
          {/* Quantity */}
          <div className="mb-8">
            <h3 className="text-lg font-medium mb-3">Quantity</h3>
            <div className="flex items-center">
              <button 
                className="bg-gray-800 px-4 py-2 rounded-l-md"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
              >
                -
              </button>
              <input 
                type="text" 
                value={quantity} 
                readOnly
                className="bg-gray-800 text-center w-16 py-2 border-x border-gray-700"
              />
              <button 
                className="bg-gray-800 px-4 py-2 rounded-r-md"
                onClick={() => setQuantity(quantity + 1)}
              >
                +
              </button>
            </div>
          </div>
          
          {/* Purchase Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button 
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              className={`py-3 px-6 rounded-md font-medium transition-colors ${
                isAddingToCart 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {isAddingToCart ? 'Adding...' : 'Add to Cart'}
            </button>
            <button 
              onClick={handleBuyNow}
              className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium border border-gray-600 transition-colors"
            >
              Buy Now
            </button>
          </div>
          
          {/* Shipping Info */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex items-center mb-3">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a1 1 0 00.9-.5l1.5-2A1 1 0 0015 7h-3V4a1 1 0 00-1-1H3zM14 7l-1.5 2H10V5h4v2z" />
              </svg>
              <span className="text-gray-300">Free Meet Up (Available at Sengkang & Orchard locations)</span>
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-300">Available Shipping</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* 产品详情选项卡 */}
      <div className="mt-16">
        <div className="border-b border-gray-700 mb-8">
          <div className="flex space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-2 font-medium ${activeTab === 'overview' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specs')}
              className={`pb-4 px-2 font-medium ${activeTab === 'specs' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Specifications
            </button>
            <button 
              onClick={() => setActiveTab('reviews')}
              className={`pb-4 px-2 font-medium ${activeTab === 'reviews' ? 'text-white border-b-2 border-green-500' : 'text-gray-400'}`}
            >
              Reviews ({controller.reviews})
            </button>
          </div>
        </div>
        
        {/* 选项卡内容 */}
        <div>
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Product Overview</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <p className="text-gray-300 mb-6">
                    The Switch Pro Controller delivers the ultimate gaming experience for Nintendo Switch owners. 
                    This premium controller features an ergonomic design that provides exceptional comfort during 
                    extended gaming sessions, with precise analog sticks and responsive buttons.
                  </p>
                  <p className="text-gray-300 mb-6">
                    With advanced motion controls and HD rumble technology, you'll feel every detail of your 
                    games. The controller's wireless connectivity ensures freedom of movement, while the 
                    long-lasting battery keeps you gaming for hours without interruption.
                  </p>
                  <p className="text-gray-300">
                    Perfect for both casual and competitive gaming, the Switch Pro Controller offers the same 
                    level of quality and precision as the original Nintendo Switch controllers, with enhanced 
                    features that make it the preferred choice for serious gamers.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Key Features</h3>
                  <ul className="space-y-4">
                    {controller.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-6 h-6 mr-2 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="font-medium">{feature}</span>
                          <p className="text-gray-400 text-sm mt-1">
                            {feature === "Built-in Rechargeable Battery" && "Built-in lithium battery with 8-10 hours of gameplay"}
                            {feature === "Wireless Bluetooth Connectivity" && "Stable Bluetooth connection for wireless gaming"}
                            {feature === "6-Axis Gyroscope Motion Controls" && "Advanced motion sensing for precise control"}
                            {feature === "USB-C Charging Support" && "USB-C port for charging and optional wired connection"}
                            {feature === "Ergonomic Design" && "Comfortable grip designed for extended gaming sessions"}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'specs' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Technical Specifications</h2>
              <div className="bg-gray-800 rounded-lg p-6">
                <table className="w-full">
                  <tbody>
                    {Object.entries(controller.specs).map(([key, value], index) => (
                      <tr key={index} className={index !== 0 ? "border-t border-gray-700" : ""}>
                        <td className="py-4 pr-4 font-medium w-1/3">{key}</td>
                        <td className="py-4 text-gray-300">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-2xl font-bold mb-6">Customer Reviews</h2>
              <div className="flex items-center mb-8">
                <div className="mr-4">
                  <div className="text-5xl font-bold">{controller.rating}</div>
                  <div className="flex text-yellow-400 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < Math.floor(controller.rating) ? "text-yellow-400" : "text-gray-600"}>★</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">Based on {controller.reviews} reviews</div>
                </div>
                <div className="flex-1 ml-6">
                  {[5, 4, 3, 2, 1].map((star) => (
                    <div key={star} className="flex items-center mb-2">
                      <div className="w-12 text-sm text-gray-400">{star} stars</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mx-2">
                        <div 
                          className="bg-yellow-400 h-2 rounded-full" 
                          style={{ 
                            width: `${star === 5 ? 80 : star === 4 ? 15 : star === 3 ? 3 : star === 2 ? 1 : 1}%` 
                          }}
                        ></div>
                      </div>
                      <div className="w-12 text-sm text-right text-gray-400">
                        {star === 5 ? '80%' : star === 4 ? '15%' : star === 3 ? '3%' : star === 2 ? '1%' : '1%'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* 示例评论 */}
              <div className="space-y-6">
                {[
                  { name: "Kevin S.", rating: 5, date: "1 week ago", comment: "This controller is absolutely perfect! The ergonomic design feels great in my hands and the motion controls are incredibly responsive. The battery life is impressive and it connects seamlessly with my Switch." },
                  { name: "Rachel L.", rating: 5, date: "2 weeks ago", comment: "I love the HD rumble feature - it adds so much immersion to my games. The controller feels premium and the wireless connection is rock solid. Worth every penny!" },
                  { name: "Daniel M.", rating: 4, date: "1 month ago", comment: "Great controller overall. The build quality is excellent and the buttons feel responsive. The only minor issue is that it's a bit heavy, but that's expected for a premium controller." }
                ].map((review, index) => (
                  <div key={index} className="border-b border-gray-700 pb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{review.name}</span>
                      <span className="text-gray-400 text-sm">{review.date}</span>
                    </div>
                    <div className="flex text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className={i < review.rating ? "text-yellow-400" : "text-gray-600"}>★</span>
                      ))}
                    </div>
                    <p className="text-gray-300">{review.comment}</p>
                  </div>
                ))}
              </div>
              
              {/* 查看更多评论按钮 */}
              <div className="mt-8 text-center">
                <button className="bg-gray-800 hover:bg-gray-700 text-white py-3 px-6 rounded-md font-medium">
                  Load More Reviews
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 相关产品 */}
      <div className="mt-20">
        <h2 className="text-2xl font-bold mb-8">You May Also Like</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayProducts.map(product => (
            <div key={product.id} className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>
              <img src={product.image} alt={product.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="font-medium mb-2">{product.name}</h3>
                <div className="flex justify-between items-center">
                  <span className="font-bold">{product.price}</span>
                  <button 
                    className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded-md text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/product/${product.id}`);
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SwitchProController;
